import { useState } from "react";
import { Box, Typography, LinearProgress } from "@mui/joy";
import { Upload } from "lucide-react";

import { formatFileSize } from "./utils";

import type { IUploadProgress } from "./hooks/useProjectAttachments";

const STATUS_COLOR: Record<IUploadProgress["status"], "primary" | "success" | "danger" | "neutral"> = {
    queued: "neutral",
    uploading: "primary",
    processing: "primary",
    done: "success",
    error: "danger",
};

function statusText(item: IUploadProgress) {
    const percent = `${Math.round(item.percent)}%`;

    switch (item.status) {
        case "queued":
            return "Queued";
        case "done":
            return "Done";
        case "error":
            return "Failed";
        case "processing":
            return `Processing · ${percent}`;
        default:
            return `${formatFileSize(item.loaded)} of ${formatFileSize(item.total)} · ${percent}`;
    }
}

interface IUploadDropzoneProps {
    uploadQueue: IUploadProgress[];
    onFiles: (files: FileList | null) => void;
    onBrowse: () => void;
}

export default function UploadDropzone({ uploadQueue, onFiles, onBrowse }: IUploadDropzoneProps) {
    const [isDragging, setIsDragging] = useState(false);

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragging(false);
        onFiles(event.dataTransfer.files);
    };

    return (
        <Box sx={{ py: 1.5 }}>
            <Box
                onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={onBrowse}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    py: 1.25,
                    px: 2,
                    cursor: "pointer",
                    border: "1px dashed",
                    borderColor: isDragging ? "primary.solidBg" : "neutral.outlinedBorder",
                    bgcolor: isDragging
                        ? "rgba(var(--standard-palette-primary-mainChannel) / 0.06)"
                        : "transparent",
                    transition: "background-color 0.15s ease, border-color 0.15s ease",
                    "&:hover": { borderColor: "neutral.outlinedHoverBorder" },
                }}
            >
                <Upload size={14} />
                <Typography level="body-xs" color="neutral">
                    Drag files here or click to browse — up to 20 MB each
                </Typography>
            </Box>

            {uploadQueue.length > 0 && (
                <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
                    {uploadQueue.map((item, index) => (
                        <Box key={`${item.name}-${index}`} sx={{ minWidth: 0 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, minWidth: 0 }}>
                                <Typography level="body-xs" noWrap sx={{ minWidth: 0 }}>
                                    {item.name}
                                </Typography>
                                <Typography level="body-xs" color="neutral" sx={{ flexShrink: 0 }}>
                                    {statusText(item)}
                                </Typography>
                            </Box>
                            <LinearProgress
                                size="sm"
                                determinate
                                value={item.percent}
                                color={STATUS_COLOR[item.status]}
                                sx={{ borderRadius: 0, mt: 0.5 }}
                            />
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}
